import React, { createContext, useCallback, useContext, useRef } from 'react';
import { getEmployeeDetailsRaw } from '../utils/employeeServiceUtils';
import { logger } from '../utils/logger';

interface BadgeColorData {
  badgeColor: string;
  level: string | undefined;
}

interface CacheEntry {
  data: BadgeColorData;
  timestamp: number;
  isError: boolean;
}

interface BadgeColorContextType {
  getBadgeColorAndLevel: (login: string) => Promise<BadgeColorData>;
}

const BadgeColorContext = createContext<BadgeColorContextType | undefined>(undefined);

const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const ERROR_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for failed requests
const MAX_CACHE_SIZE = 1000;

/**
 * Provider component that manages badge color caching
 * Prevents duplicate API calls for the same login
 */
export const BadgeColorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Cache to store badge color data by login with TTL
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());

  // Track in-flight requests to prevent duplicate calls
  const pendingRequestsRef = useRef<Map<string, Promise<BadgeColorData>>>(new Map());

  const evictExpiredEntries = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cacheRef.current.entries());

    for (const [key, entry] of entries) {
      const ttl = entry.isError ? ERROR_CACHE_TTL : CACHE_TTL;
      if (now - entry.timestamp > ttl) {
        cacheRef.current.delete(key);
      }
    }
  }, []);

  const evictOldestEntries = useCallback(() => {
    if (cacheRef.current.size <= MAX_CACHE_SIZE) return;

    const entries = Array.from(cacheRef.current.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => cacheRef.current.delete(key));
  }, []);

  const getBadgeColorAndLevel = useCallback(
    async (login: string): Promise<BadgeColorData> => {
      evictExpiredEntries();

      // Return cached data if available and not expired
      const cached = cacheRef.current.get(login);
      if (cached) {
        const ttl = cached.isError ? ERROR_CACHE_TTL : CACHE_TTL;
        if (Date.now() - cached.timestamp <= ttl) {
          return cached.data;
        }
        cacheRef.current.delete(login);
      }

      // Return pending request if one exists
      if (pendingRequestsRef.current.has(login)) {
        return pendingRequestsRef.current.get(login)!;
      }

      // Create new request and store it immediately to prevent race conditions
      const request = (async () => {
        try {
          const response = await getEmployeeDetailsRaw(login);
          const data: BadgeColorData = {
            badgeColor: response.employee.basicInfo.badgeColor || '',
            level: response.employee.job?.jobLevel,
          };

          // Cache the successful result
          evictOldestEntries();
          cacheRef.current.set(login, {
            data,
            timestamp: Date.now(),
            isError: false,
          });

          return data;
        } catch (error) {
          logger.error('Error fetching badge color:', { login, error });

          // Cache error result with shorter TTL for retry
          const emptyData: BadgeColorData = {
            badgeColor: '',
            level: undefined,
          };

          evictOldestEntries();
          cacheRef.current.set(login, {
            data: emptyData,
            timestamp: Date.now(),
            isError: true,
          });

          return emptyData;
        } finally {
          // Remove from pending requests
          pendingRequestsRef.current.delete(login);
        }
      })();

      // Store pending request immediately after creation to ensure atomicity
      pendingRequestsRef.current.set(login, request);

      return request;
    },
    [evictExpiredEntries, evictOldestEntries]
  );

  return <BadgeColorContext.Provider value={{ getBadgeColorAndLevel }}>{children}</BadgeColorContext.Provider>;
};

/**
 * Hook to access badge color context
 * @throws Error if used outside BadgeColorProvider
 */
export const useBadgeColorContext = (): BadgeColorContextType => {
  const context = useContext(BadgeColorContext);
  if (!context) {
    throw new Error('useBadgeColorContext must be used within BadgeColorProvider');
  }
  return context;
};
